import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Sparkles, Sun, Palette, ShieldCheck } from "lucide-react";
import heroPPF from "@/assets/hero-ppf.jpg";
import heroCeramic from "@/assets/hero-ceramic.jpg";
import heroWindowTint from "@/assets/hero-window-tint.webp";
import heroVinylWraps from "@/assets/hero-vinyl-wraps.webp";
import heroWindshield from "@/assets/hero-windshield.webp";

const Services = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Automotive Protection Services Las Vegas | Battle Born PPF</title>
        <meta name="description" content="Premium automotive protection services in Las Vegas. XPEL certified PPF, ceramic coatings, window tint, vinyl wraps & windshield protection for luxury vehicles." />
      </Helmet>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Premium Protection Services
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive automotive protection solutions designed for discerning vehicle owners who demand the best
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Paint Protection Film */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={heroPPF} 
                  alt="XPEL Paint Protection Film Las Vegas - PPF Installation" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-8">
                <Shield className="w-12 h-12 text-accent mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-3">Paint Protection Film</h2>
                <p className="text-muted-foreground mb-6">
                  XPEL Ultimate Plus self-healing film protects against rock chips, scratches, and environmental damage while maintaining factory finish.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/paint-protection-film">View Details</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Ceramic Coatings */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={heroCeramic} 
                  alt="XPEL Fusion Plus Ceramic Coating Las Vegas" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-8">
                <Sparkles className="w-12 h-12 text-accent mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-3">Ceramic Coatings</h2>
                <p className="text-muted-foreground mb-6">
                  Advanced nano-ceramic coatings deliver unmatched gloss, hydrophobic protection, and long-lasting durability for your vehicle's finish.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/ceramic-coatings">View Details</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Ceramic Window Tint */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={heroWindowTint} 
                  alt="XPEL Window Tint Las Vegas - Ceramic Film" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-8">
                <Sun className="w-12 h-12 text-accent mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-3">Window Tint</h2>
                <p className="text-muted-foreground mb-6">
                  Premium XPEL films for automotive and residential use. Get maximum UV protection, heat rejection, and privacy.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/window-tint">View Details</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Vinyl Wraps & Graphics */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={heroVinylWraps} 
                  alt="Vinyl Wraps & Graphics Las Vegas" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-8">
                <Palette className="w-12 h-12 text-accent mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-3">Vinyl Wraps & Graphics</h2>
                <p className="text-muted-foreground mb-6">
                  Full color change wraps and custom-designed graphics for personal vehicles, commercial branding, and fleet advertising.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/vinyl-wraps">View Details</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Windshield Protection */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={heroWindshield} 
                  alt="XPEL Windshield Protection Film Las Vegas" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-8">
                <ShieldCheck className="w-12 h-12 text-accent mb-4" />
                <h2 className="text-2xl font-heading font-bold mb-3">Windshield Protection Film</h2>
                <p className="text-muted-foreground mb-6">
                  Durable, optically clear XPEL film that absorbs impacts from rock chips, preventing costly cracks and pitting.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/windshield-protection">View Details</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Investment?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
